import { useAuth } from "@clerk/react";
import { useCallback, useMemo } from "react";

import { hostedAuthEnabled } from "../cloud/clerkRuntime";
import { createHostedApiClient, HostedApiError, type HostedApiClient } from "./api";

/**
 * Builds a hosted API client bound to the current Clerk session. Kept as a hook
 * so token reads always go through Clerk's live session rather than a cached
 * string, and so a signed-out browser produces a client that fails fast with
 * `unauthenticated` instead of sending an anonymous request.
 */
export function useHostedApiClient(): HostedApiClient {
  const { getToken } = useAuth({ treatPendingAsSignedOut: false });

  // The Worker verifies this with Clerk's backend `verifyToken` and reads `sub`,
  // so it wants the default session token. The relay JWT template is a
  // different audience and must not be sent here.
  const readClerkToken = useCallback(() => getToken(), [getToken]);

  return useMemo(() => createHostedApiClient({ readClerkToken }), [readClerkToken]);
}

/**
 * A `fetch` that carries the current Clerk session bearer and same-origin
 * cookies. Needed by the Login with ChatGPT SDK, which owns its own routes under
 * `/api/chatgpt/*` but sits behind the Worker's owner check.
 */
export function useHostedAuthorizedFetch(): typeof globalThis.fetch {
  const { getToken } = useAuth({ treatPendingAsSignedOut: false });

  return useCallback(
    async (input: RequestInfo | URL, init?: RequestInit) => {
      const token = await getToken().catch(() => null);
      const headers = new Headers(init?.headers);
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return globalThis.fetch(input, { ...init, headers, credentials: "same-origin" });
    },
    [getToken],
  ) as typeof globalThis.fetch;
}

/**
 * Whether this build can talk to the hosted Worker at all. The Worker verifies
 * the default Clerk session token, so a publishable key on the hosted origin is
 * the whole requirement — the relay URL and JWT template that T3 Connect needs
 * are irrelevant here and must not gate this.
 */
export function isHostedApiConfigured(): boolean {
  return hostedAuthEnabled();
}

export function hostedApiErrorMessage(error: unknown): string {
  if (error instanceof HostedApiError) {
    return error.message;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "An unexpected error occurred.";
}

export function hostedApiErrorCode(error: unknown): HostedApiError["code"] | null {
  return error instanceof HostedApiError ? error.code : null;
}
